import { useCallback, useEffect, useState } from "react"

import { facilitiesApi, type FacilityDirectoryItem } from "../lib/api/handlers/facilities"
import type { FacilityClassification } from "./moduleRegistry"
import { readConfigEntry, saveConfigEntry } from "../lib/offline/db"
import { isRuntimeOfflineEnabled } from "../lib/offline/runtime"
import { isNavigatorOffline } from "../lib/offline/referenceCache"
import { getOfflineCachePolicy } from "../lib/offline/cachePolicy"

export type FacilityRecord = {
  id: string
  name: string
  classification: FacilityClassification | string | null
}

let facilityCache: FacilityRecord[] | null = null
let facilityPromise: Promise<FacilityRecord[]> | null = null
const FACILITY_CACHE_KEY = "facilities/directory"

function normalizeRecords(payload: readonly FacilityDirectoryItem[] | null | undefined): FacilityRecord[] {
  if (!payload) return []
  const seen = new Set<string>()
  const normalized: FacilityRecord[] = []
  payload.forEach((item) => {
    if (!item) return
    const id = typeof item.id === "string" ? item.id.trim() : ""
    if (!id || seen.has(id)) return
    const nameCandidate = typeof item.name === "string" ? item.name.trim() : ""
    const name = nameCandidate || id
    const classificationCandidate = typeof item.classification === "string" ? item.classification.trim() : ""
    const classification = classificationCandidate || null
    normalized.push({ id, name, classification })
    seen.add(id)
  })
  return normalized
}

const loadFacilitiesFromOfflineCache = async (): Promise<FacilityRecord[] | null> => {
  const entry = await readConfigEntry<FacilityRecord[]>(FACILITY_CACHE_KEY)
  return entry?.data ?? null
}

const persistFacilitiesToOfflineCache = async (records: FacilityRecord[]) => {
  try {
    const ttlMs = getOfflineCachePolicy().facilityTtlMs
    await saveConfigEntry({
      type: FACILITY_CACHE_KEY,
      data: records,
      updatedAt: Date.now(),
      expiresAt: ttlMs ? Date.now() + ttlMs : null,
    })
  } catch (error) {
    console.warn("Failed to persist facilities offline cache", error)
  }
}

export async function fetchFacilityDirectory(force = false): Promise<FacilityRecord[]> {
  if (!force) {
    if (facilityCache) {
      return facilityCache
    }
    if (facilityPromise) {
      return facilityPromise
    }
  }

  const maybeOffline = isNavigatorOffline() || isRuntimeOfflineEnabled()
  if (maybeOffline) {
    const cached = await loadFacilitiesFromOfflineCache()
    if (cached) {
      facilityCache = cached
      return cached
    }
  }

  const request = facilitiesApi
    .list()
    .then(async (result) => {
      const normalized = normalizeRecords(result)
      facilityCache = normalized
      facilityPromise = null
      await persistFacilitiesToOfflineCache(normalized)
      return normalized
    })
    .catch(async (error) => {
      facilityPromise = null
      const cached = await loadFacilitiesFromOfflineCache()
      if (cached) {
        facilityCache = cached
        return cached
      }
      throw error
    })

  facilityPromise = request
  return request
}

export function clearFacilityDirectoryCache() {
  facilityCache = null
  facilityPromise = null
}

export function useFacilityDirectory() {
  const [facilities, setFacilities] = useState<FacilityRecord[]>(() => facilityCache ?? [])
  const [loading, setLoading] = useState(!facilityCache)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    async (force = false) => {
      try {
        setLoading(true)
        const data = await fetchFacilityDirectory(force)
        setFacilities(data)
        setError(null)
        return data
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load facilities"
        setError(message)
        if (!facilityCache) {
          setFacilities([])
        }
        throw err
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (!facilityCache) {
      load().catch(() => {
        /* handled via state */
      })
    }
  }, [load])

  const refresh = useCallback(async () => {
    await load(true).catch(() => {
      /* handled via state */
    })
  }, [load])

  return { facilities, loading, error, refresh }
}

export function findFacilityById(
  id: string | null | undefined,
  facilities: readonly FacilityRecord[] | null | undefined = facilityCache ?? undefined,
): FacilityRecord | undefined {
  if (!id) return undefined
  const source = facilities ?? undefined
  if (!source) return undefined
  return source.find((facility) => facility.id === id)
}
